import { TableCell, TableRow } from '@material-ui/core';
import { isEmpty } from 'lodash';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'src/modules/date-helpers';
import { DetailCarDTO, SourceType } from 'src/modules/generated/api';
import { bodyTypes, damageTypes, getLabel } from 'src/modules/labels';

import CarDetailTable from './CarDetailTable';
import CarDetailTableHeadCell from './CarDetailTableHeadCell';
import UnitValue from './UnitValue';

type CarDetailAdditionalCarInfoProps = {
  car: DetailCarDTO;
  disableElevation?: boolean;
  className?: string;
};

const CarDetailAdditionalCarInfo = ({ car, disableElevation = false, className }: CarDetailAdditionalCarInfoProps) => {
  const { t } = useTranslation();
  // UCP cars dont have all data, therefore we hide some fields if empty
  const isUcp = car.source === SourceType.AudiUcp;

  // transform / to space for better readability and remove double words
  const formatString = (str: string): string =>
    str
      .split('/')
      .filter((word, index, self) => self.indexOf(word) === index)
      .join(' ');

  return (
    <CarDetailTable title={t('car.additionalCarInfo')} disableElevation={disableElevation} className={className}>
      {(!isUcp || car.constructionDate) && (
        <TableRow>
          <CarDetailTableHeadCell>{t('car.constructionDate')}</CarDetailTableHeadCell>
          <TableCell>
            {car.constructionDate ? formatDate(car.constructionDate, 'dd.MM.yyyy') : t('common.unavailable')}
          </TableCell>
        </TableRow>
      )}
      {(!isUcp || car.modelyear != null) && (
        <TableRow>
          <CarDetailTableHeadCell>{t('car.modelyear')}</CarDetailTableHeadCell>
          <TableCell>{car.modelyear ?? t('common.unavailable')}</TableCell>
        </TableRow>
      )}
      <TableRow>
        <CarDetailTableHeadCell>{t('car.upholsteryColor')}</CarDetailTableHeadCell>
        <TableCell>
          {car.upholsteryColorText ? formatString(car.upholsteryColorText) : t('common.unavailable')}
        </TableCell>
      </TableRow>
      {(!isUcp || car.normalized?.bodyType) && (
        <TableRow>
          <CarDetailTableHeadCell>{t('car.bodyType')}</CarDetailTableHeadCell>
          <TableCell>
            {car.normalized?.bodyType ? getLabel(bodyTypes, car.normalized.bodyType) : t('common.unavailable')}
          </TableCell>
        </TableRow>
      )}
      <TableRow>
        <CarDetailTableHeadCell>{t('car.damageType')}</CarDetailTableHeadCell>
        <TableCell>
          {!isEmpty(car.damageType) ? car.damageType?.map((type) => getLabel(damageTypes, type)).join(', ') : ''}
        </TableCell>
      </TableRow>
      {(!isUcp || car.numberOfPreviousOwners != null) && (
        <TableRow>
          <CarDetailTableHeadCell>{t('car.numPrevOwners')}</CarDetailTableHeadCell>
          <TableCell>{car.numberOfPreviousOwners ?? t('common.unavailable')}</TableCell>
        </TableRow>
      )}
      <TableRow>
        <CarDetailTableHeadCell>{t('car.cylinderCapacity')}</CarDetailTableHeadCell>
        <TableCell>
          {car.normalized?.augmented?.cylinderCapacity || car.engineCylinderCapacity ? (
            <UnitValue value={car.normalized?.augmented?.cylinderCapacity || car.engineCylinderCapacity} unit="ccm" />
          ) : (
            <div>{t('common.unavailable')}</div>
          )}
        </TableCell>
      </TableRow>
      {(!isUcp || car.normalized?.augmented?.consumptionWltp) && (
        <TableRow>
          <CarDetailTableHeadCell>{t('car.consumptionWltp')}</CarDetailTableHeadCell>
          <TableCell>{car.normalized?.augmented?.consumptionWltp ?? t('common.unavailable')}</TableCell>
        </TableRow>
      )}
      <TableRow>
        <CarDetailTableHeadCell>{t('car.emission_class')}</CarDetailTableHeadCell>
        <TableCell>{car.normalized?.augmented?.emissionClass ?? t('common.unavailable')}</TableCell>
      </TableRow>
      <TableRow>
        <CarDetailTableHeadCell>{t('car.lastGeneralInspection')}</CarDetailTableHeadCell>
        <TableCell>{formatDate(car.lastGeneralInspection!, 'dd.MM.yyyy')}</TableCell>
      </TableRow>
      {(!isUcp || car.lastEmissionTest) && (
        <TableRow>
          <CarDetailTableHeadCell>{t('car.lastEmissionTest')}</CarDetailTableHeadCell>
          <TableCell>{formatDate(car.lastEmissionTest!, 'dd.MM.yyyy')}</TableCell>
        </TableRow>
      )}
    </CarDetailTable>
  );
};

export default memo(CarDetailAdditionalCarInfo);
